import React, { useState, useContext } from "react";
import { domainsPerDeliveryAccountsLimits } from "@/config";
import s from "./deliveryAccountCard.scss";
import t, { getAccountTypeTranslation } from "Utils/translation";
import { useHistory } from "react-router-dom";
import { more, disable, enable, trash } from "Icons/index";
import DropdownMenu from "Components/dropdownMenu/DropdownMenu";
import ButtonIcon from "Components/buttonIcon/ButtonIcon";
import StatusBadge from "../statusBadge/StatusBadge";
import { AuthContext } from "../../App";
import cx from "classnames";

const formatter = new Intl.NumberFormat(navigator.language || navigator.userLanguage, {
  maximumFractionDigits: 0,
});

const DeliveryAccountCard = ({
  accountData = {},
  onStatusChangeButtonClick,
  onClick,
  statusChangeButtonLabel,
  isStatusUpdating = false,
}) => {
  const history = useHistory();
  const { isLoggedUserAdmin, getLoggedUser } = useContext(AuthContext);

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const loggedUser = getLoggedUser();
  const isManager = loggedUser.type === "Manager";

  let infoGrid = [
    { label: "delivery.accounts.label.type", value: getAccountTypeTranslation(accountData.type) },
    { label: "delivery.accounts.label.plan", value: t(`delivery.plan.${accountData.plan}`) },
    { label: "delivery.accounts.label.availableMessages", value: formatter.format(accountData.remaining_messages) },
    {
      label: "delivery.accounts.label.domainsCount",
      value:
        String(accountData.domains_count) +
        "/" +
        String(domainsPerDeliveryAccountsLimits[accountData.plan === "free" ? "free" : "paid"]),
      warning: accountData.domains_count === 0,
    },
  ];

  if (accountData.delete_on) {
    infoGrid.push({ label: "delivery.accounts.label.deletingOn", value: accountData.delete_on, warning: true });
  } else if (accountData.next_renewal_date) {
    infoGrid.push({ label: "delivery.accounts.label.renewalDate", value: accountData.next_renewal_date });
  }

  if (isLoggedUserAdmin()) {
    infoGrid.push({ label: "delivery.accounts.label.customerName", value: accountData.customer_fullname });
  }

  const isFreeWithPendingOrder = accountData.plan === "free" && accountData.has_pending_order;

  return (
    <div
      className="rounded border border-solid border-[--bg-d5] bg-white flex flex-col cursor-pointer gap-1"
      onClick={(e) => {
        e.preventDefault();

        const menuSectionItem = e.target.closest("#delivery-account-card-menu-section");

        if (!menuSectionItem && window.getSelection().isCollapsed) {
          onClick();
        }
      }}
    >
      <div className="flex flex-grow gap-1 px-3 pt-3 pb-1">
        <div className="pt-1">
          <StatusBadge isManager={isManager} isUpdating={isStatusUpdating} data={accountData} type="delivery-account" />
        </div>

        <div className="flex-grow">
          {/* header info */}
          <div className="col-span-2 px-1 mb-3">
            <div className="text-lg font-semibold">{accountData.description}</div>

            <div className="text-sm text-[--text-a60]">
              {isLoggedUserAdmin() ? `ID: ${accountData.id}` : accountData.username}
            </div>
          </div>

          {/* content grid */}
          <div className="grid grid-cols-2 gap-[1px] gap-y-2 grid-rows-3">
            {infoGrid.map((i) => (
              <div
                key={i.label}
                className={cx("p-1 rounded relative z-10", {
                  // "bg-gray-50": i.warning || i.alert,
                  "bg-[rgba(250,187,5,0.30)]": i.warning || i.alert,
                })}
              >
                {i.alert ? (
                  <div className="text-sm">{t(i.alert)}</div>
                ) : (
                  <>
                    <div className="text-sm font-semibold">{i.value}</div>
                    <div className="text-xs text-[--text-a60]">{t(i.label)}</div>
                  </>
                )}
              </div>
            ))}
          </div>
          {/* 
          {accountData.overdeliviery ? (
            <div>
              <div className={s.value}>{accountData.type}</div>
              <div className={s.description}>{t("delivery.accounts.label.extraDeliveries")}</div>
            </div>
          ) : null} */}
        </div>

        <div className={s.menuSection} id="delivery-account-card-menu-section">
          {accountData.status_detail !== "wait_pay" && (
            <DropdownMenu
              isOpen={isMenuOpen}
              onOpenChange={setIsMenuOpen}
              actions={[
                {
                  onClick: () => onStatusChangeButtonClick(accountData),
                  icon: accountData.status ? disable : enable,
                  label: statusChangeButtonLabel,
                },
                {
                  onClick: () => {
                    if (isLoggedUserAdmin()) {
                      history.push(
                        `/customers/${accountData.customer_code}/delivery/accounts/${accountData.id}/service-management`
                      );
                    } else {
                      history.push(`/delivery/accounts/${accountData.id}/service-management`);
                    }
                  },
                  icon: trash,
                  label: t("button.delete"),
                },
              ]}
            >
              <div style={isStatusUpdating ? { visibility: "hidden" } : {}}>
                <ButtonIcon icon={more} />
              </div>
            </DropdownMenu>
          )}
        </div>
      </div>

      {accountData.status_detail === "wait_pay" || isFreeWithPendingOrder ? (
        <div className="w-full py-[1px] border-b-rounded bg-[rgba(250,187,5,0.30)] text-sm text-center text-yellow-900">
          {isManager
            ? t("delivery.account.status.waitingCustomerApproval")
            : t("delivery.accounts.label.waiting_payment")}
        </div>
      ) : null}
    </div>
  );
};

export default DeliveryAccountCard;
